import { buildSimulatorCategoryAttributeCache } from './simulatorFilter.helpers';
import {
    buildCategoryApplyFiltersFromPanelState,
    emptySimulatorCategoryApplyFilter,
    updateSimulatorCategoryApplyFilter,
} from './simulatorFilterExcludeInclude.helpers';
import type {
    SimulatorAppliedFilters,
    SimulatorCategoryApplyFilter,
    SimulatorFilterCategory,
} from './SimulatorFilterPanel.types';
import type { SimulatorFilterOption } from './SimulatorFilterCategoryPanel';
import type { SimulatorShowOnlyState } from './simulatorFilter.helpers';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

type SelectAllChangeArgs = {
    checked: boolean;
    selectedCategory: SimulatorFilterCategory | null;
    debouncedCategorySearch: string;
    categorySelections: Record<string, string[]>;
    selectedCategoryOptions: SimulatorFilterOption[];
    pendingClearUsedSelectAllRef: MutableRefObject<string | null>;
    setCategorySelectAll: Dispatch<SetStateAction<Record<string, boolean>>>;
    setCategorySelectAllPreviewOptions: Dispatch<SetStateAction<Record<string, SimulatorFilterOption[]>>>;
    setCategoryUsedSelectAll: Dispatch<SetStateAction<Record<string, boolean>>>;
    setCategoryDraftApplyFilters: Dispatch<SetStateAction<Record<string, SimulatorCategoryApplyFilter>>>;
    setCategorySelections: Dispatch<SetStateAction<Record<string, string[]>>>;
    syncSelectAllPreviewForCategory: (categoryKey: string, options: SimulatorFilterOption[]) => void;
};

export function runCategorySelectAllChange({
    checked,
    selectedCategory,
    debouncedCategorySearch,
    categorySelections,
    selectedCategoryOptions,
    pendingClearUsedSelectAllRef,
    setCategorySelectAll,
    setCategorySelectAllPreviewOptions,
    setCategoryUsedSelectAll,
    setCategoryDraftApplyFilters,
    setCategorySelections,
    syncSelectAllPreviewForCategory,
}: SelectAllChangeArgs) {
    if (!selectedCategory) return;

    const categoryKey = selectedCategory.key;

    if (!checked) {
        setCategorySelectAll((current) => ({ ...current, [categoryKey]: false }));
        setCategorySelectAllPreviewOptions((current) => {
            const next = { ...current };
            delete next[categoryKey];
            return next;
        });
        if (debouncedCategorySearch) {
            const visibleIds = new Set(selectedCategoryOptions.map((option) => option.id));
            setCategorySelections((current) => ({
                ...current,
                [categoryKey]: (current[categoryKey] ?? []).filter((id) => !visibleIds.has(id)),
            }));
        } else {
            pendingClearUsedSelectAllRef.current = categoryKey;
            queueMicrotask(() => {
                if (pendingClearUsedSelectAllRef.current === categoryKey) {
                    setCategoryUsedSelectAll((current) => {
                        const next = { ...current };
                        delete next[categoryKey];
                        return next;
                    });
                    setCategoryDraftApplyFilters((current) => {
                        const next = { ...current };
                        delete next[categoryKey];
                        return next;
                    });
                    pendingClearUsedSelectAllRef.current = null;
                }
            });
            setCategorySelections((current) => ({
                ...current,
                [categoryKey]: [],
            }));
        }
        return;
    }

    if (debouncedCategorySearch) {
        const nextIds = new Set(categorySelections[categoryKey] ?? []);
        selectedCategoryOptions.forEach((option) => nextIds.add(option.id));
        setCategorySelections((current) => ({
            ...current,
            [categoryKey]: Array.from(nextIds),
        }));
        return;
    }

    setCategorySelectAll((current) => ({ ...current, [categoryKey]: true }));
    setCategoryUsedSelectAll((current) => ({ ...current, [categoryKey]: true }));
    setCategoryDraftApplyFilters((current) => ({
        ...current,
        [categoryKey]: emptySimulatorCategoryApplyFilter(),
    }));
    syncSelectAllPreviewForCategory(categoryKey, selectedCategoryOptions);
    setCategorySelections((current) => ({
        ...current,
        [categoryKey]: [],
    }));
}

type ApplyFiltersArgs = {
    appliedCategoryTotalCounts: Record<string, number>;
    selectedCategory: SimulatorFilterCategory | null;
    fetchedCategoryTotalCount: number;
    categorySelections: Record<string, string[]>;
    categorySelectAll: Record<string, boolean>;
    categoryUsedSelectAll: Record<string, boolean>;
    categoryDraftApplyFilters: Record<string, SimulatorCategoryApplyFilter>;
    categorySelectAllPreviewOptions: Record<string, SimulatorFilterOption[]>;
    draftShowOnly: SimulatorShowOnlyState;
    onApply?: (filters: SimulatorAppliedFilters) => void | Promise<boolean>;
};

export async function runFilterPanelApply({
    appliedCategoryTotalCounts,
    selectedCategory,
    fetchedCategoryTotalCount,
    categorySelections,
    categorySelectAll,
    categoryUsedSelectAll,
    categoryDraftApplyFilters,
    categorySelectAllPreviewOptions,
    draftShowOnly,
    onApply,
}: ApplyFiltersArgs) {
    const totalCountByCategory: Record<string, number> = { ...appliedCategoryTotalCounts };
    if (selectedCategory && fetchedCategoryTotalCount > 0) {
        totalCountByCategory[selectedCategory.key] = fetchedCategoryTotalCount;
    }

    const categoryAttributeCache = buildSimulatorCategoryAttributeCache({}, totalCountByCategory);
    const draftApplyFilters = buildCategoryApplyFiltersFromPanelState(
        categorySelections,
        categorySelectAll,
        categoryUsedSelectAll,
        categoryDraftApplyFilters,
    );

    await onApply?.({
        categorySelections: { ...categorySelections },
        categorySelectAll: { ...categorySelectAll },
        categoryUsedSelectAll: { ...categoryUsedSelectAll },
        ...(draftApplyFilters
            ? {
                  categoryDraftApplyFilters: draftApplyFilters,
                  categoryApplyFilters: draftApplyFilters,
              }
            : {}),
        categorySelectAllPreviewOptions: { ...categorySelectAllPreviewOptions },
        showOnly: draftShowOnly,
        categoryTotalCounts: totalCountByCategory,
        categoryAttributeCache,
    });
}

export function runCategoryOptionToggle(
    categoryKey: string,
    optionId: string,
    checked: boolean,
    fromSelectAll: boolean,
    categoryUsedSelectAll: Record<string, boolean>,
    pendingClearUsedSelectAllRef: MutableRefObject<string | null>,
    setCategoryUsedSelectAll: Dispatch<SetStateAction<Record<string, boolean>>>,
    setCategoryDraftApplyFilters: Dispatch<SetStateAction<Record<string, SimulatorCategoryApplyFilter>>>,
) {
    const usedSelectAll = fromSelectAll || Boolean(categoryUsedSelectAll[categoryKey]);

    if (fromSelectAll && !checked) {
        pendingClearUsedSelectAllRef.current = null;
        setCategoryUsedSelectAll((current) => ({ ...current, [categoryKey]: true }));
    }

    setCategoryDraftApplyFilters((current) => {
        const nextApplyFilter = updateSimulatorCategoryApplyFilter(
            current[categoryKey],
            optionId,
            checked,
            usedSelectAll,
        );
        const next = { ...current };
        if (nextApplyFilter) {
            next[categoryKey] = nextApplyFilter;
        } else {
            delete next[categoryKey];
        }
        return next;
    });
}

export function runCategorySelectionChange(
    categoryKey: string,
    ids: string[],
    fromSelectAll: boolean,
    categoryUsedSelectAll: Record<string, boolean>,
    pendingClearUsedSelectAllRef: MutableRefObject<string | null>,
    setCategoryUsedSelectAll: Dispatch<SetStateAction<Record<string, boolean>>>,
    setCategoryDraftApplyFilters: Dispatch<SetStateAction<Record<string, SimulatorCategoryApplyFilter>>>,
    setCategorySelectAll: Dispatch<SetStateAction<Record<string, boolean>>>,
    setCategorySelections: Dispatch<SetStateAction<Record<string, string[]>>>,
) {
    const usedSelectAll = fromSelectAll || Boolean(categoryUsedSelectAll[categoryKey]);

    if (fromSelectAll && ids.length > 0) {
        pendingClearUsedSelectAllRef.current = null;
        setCategoryUsedSelectAll((current) => ({ ...current, [categoryKey]: true }));
    }

    if (!usedSelectAll) {
        setCategoryDraftApplyFilters((current) => {
            const next = { ...current };
            if (ids.length > 0) {
                next[categoryKey] = { inclusion: [...ids] };
            } else {
                delete next[categoryKey];
            }
            return next;
        });
    }

    setCategorySelectAll((current) => ({ ...current, [categoryKey]: false }));
    setCategorySelections((current) => ({
        ...current,
        [categoryKey]: ids,
    }));
}
